import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { MobileFind } from './pages/mobile/MobileFind';
import { MobileHome } from './pages/mobile/MobileHome';
import { MobileLayout } from './pages/mobile/MobileLayout';
import { MobilePutAway } from './pages/mobile/MobilePutAway';
import { MobileScan } from './pages/mobile/MobileScan';
import { InboundShipmentDocument } from './pages/InboundShipmentDocument';
import { useAuth } from './auth';
import { Layout } from './components/Layout';
import { AddStock } from './pages/AddStock';
import { Channels } from './pages/Channels';
import { Dashboard } from './pages/Dashboard';
import { ItemDetail } from './pages/ItemDetail';
import { ItemsHub, InventoryRedirect } from './pages/ItemsHub';
import { NewItem } from './pages/NewItem';
import { Locations } from './pages/Locations';
import { LotDetail } from './pages/LotDetail';
import { Login } from './pages/Login';
import { PurchaseDetail } from './pages/PurchaseDetail';
import { PurchaseDocument } from './pages/PurchaseDetail';
import { ItemReceipt } from './pages/ItemReceipt';
import { ItemReceipts } from './pages/ItemReceipts';
import { InboundShipments } from './pages/InboundShipments';
import { InboundShipmentDetail } from './pages/InboundShipmentDetail';
import { BillDetail, Bills } from './pages/Bills';
import { BuyList } from './pages/BuyList';
import { TransferDetail, Transfers } from './pages/Transfers';
import { Pricing } from './pages/Pricing';
import { DealRadar } from './pages/DealRadar';
import { PriceMovers } from './pages/PriceMovers';
import { FulfilmentQueue, SaleDetail, SaleDocument, SalePickList, Sales } from './pages/Sales';
import { Sync } from './pages/Sync';
import { ReportView, Reports } from './pages/Reports';
import { PutAway } from './pages/PutAway';
import { Purchases } from './pages/Purchases';
import { Settings } from './pages/Settings';
import { Stocktaking } from './pages/Stocktaking';
import { StocktakeDetail, StocktakeSheet } from './pages/StocktakeDetail';
import { SupplierDetail } from './pages/SupplierDetail';
import { Suppliers } from './pages/Suppliers';
import { CustomerDetail, Customers } from './pages/Customers';
import { Spinner } from './ui';

export function App() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Login />;

  const canSeeCosts = user.role !== 'picker';
  return (
    <Routes>
      {/* Printable documents stand alone, with no navigation bar, so they print and save as clean pages. */}
      <Route path="stocktaking/:id/sheet" element={<StocktakeSheet />} />
      {canSeeCosts && <Route path="inbound-shipments/:id/document" element={<InboundShipmentDocument />} />}
      {canSeeCosts && (
        <>
          <Route path="sales/:id/document/:kind" element={<SaleDocument />} />
          <Route path="sales/:id/pick" element={<SalePickList />} />
          <Route path="purchases/:id/document/:kind" element={<PurchaseDocument />} />
        </>
      )}
      <Route path="m" element={<MobileLayout />}>
        <Route index element={<MobileHome />} />
        <Route path="find" element={<MobileFind />} />
        {user.role !== 'picker' && (
          <>
            <Route path="scan" element={<MobileScan />} />
            <Route path="put-away" element={<MobilePutAway />} />
          </>
        )}
      </Route>
      <Route element={<Layout />}>
        <Route index element={<PhoneRedirect>{canSeeCosts ? <Dashboard /> : <Navigate to="/items?view=lots" replace />}</PhoneRedirect>} />
        <Route path="inventory" element={<InventoryRedirect />} />
        <Route path="items" element={<ItemsHub />} />
        <Route path="lots/:id" element={<LotDetail />} />
        <Route path="locations" element={<Locations />} />
        <Route path="stocktaking" element={<Stocktaking />} />
        <Route path="stocktaking/:id" element={<StocktakeDetail />} />
        {canSeeCosts && (
          <>
            <Route path="items/new" element={<NewItem />} />
            <Route path="items/:id" element={<ItemDetail />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="suppliers/:id" element={<SupplierDetail />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="put-away" element={<PutAway />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="bills" element={<Bills />} />
            <Route path="bills/:id" element={<BillDetail />} />
            <Route path="transfers" element={<Transfers />} />
            <Route path="transfers/:id" element={<TransferDetail />} />
            <Route path="buy-list" element={<BuyList />} />
            <Route path="deal-radar" element={<DealRadar />} />
            <Route path="price-movers" element={<PriceMovers />} />
            <Route path="sync" element={<Sync />} />
            <Route path="sales" element={<Sales />} />
            <Route path="sales/fulfilment" element={<FulfilmentQueue />} />
            <Route path="sales/:id" element={<SaleDetail />} />
            <Route path="reports" element={<Reports />} />
            <Route path="reports/:key" element={<ReportView />} />
            <Route path="purchases" element={<Purchases />} />
            <Route path="purchases/:id" element={<PurchaseDetail />} />
            <Route path="item-receipts" element={<ItemReceipts />} />
            <Route path="inbound-shipments" element={<InboundShipments />} />
            <Route path="inbound-shipments/:id" element={<InboundShipmentDetail />} />
            <Route path="item-receipts/:id" element={<ItemReceipt />} />
            <Route path="batches/:id/add" element={<AddStock />} />
            <Route path="channels" element={<Channels />} />
          </>
        )}
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** A phone opening the site lands on the phone version, once per visit. "Full site" there turns this off. */
function PhoneRedirect({ children }: { children: ReactNode }) {
  let skip = false;
  try {
    skip = sessionStorage.getItem('bl_full_site') === '1';
  } catch {
    skip = false;
  }
  const phone = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches && navigator.maxTouchPoints > 0;
  return phone && !skip ? <Navigate to="/m" replace /> : <>{children}</>;
}
